
































































































































































































































































































































import { Component, Vue } from 'vue-property-decorator';
import CoinflowCardNumberInput from './card-form/CoinflowCardNumberInput.vue';
import CoinflowCvvInput from './card-form/CoinflowCvvInput.vue';
import { IGameItemForSaleByGala } from '~/store/games/types';
import { CoinflowProductType } from '~/types/coinflow';
import { IUser } from '~/store/profile/types';
import CoinflowPurchase from '~/queries/coinflowPurchase.gql';
import { IDocumentsAccepted } from '~/types/legal-documents';
import { getParsedCookie } from '~/utils';
import countries from '~/utils/countries.json';
import { CoinflowCardValidationResponse } from './lib/common';
import VueGoogleAutocomplete from './VueGoogleAutocomplete.vue';
import CoinflowGooglePayButton from './mobile-wallet/CoinflowGooglePayButton.vue';
import CoinflowApplePayButton from './mobile-wallet/CoinflowApplePayButton.vue';

enum CoinflowPaymentMethodKey {
  CreditCard = 'credit-card',
  GooglePay = 'google-pay',
  ApplePay = 'apple-pay',
}

interface CoinflowPaymentMethod {
  key: string;
  label: string;
  icon: string;
  alt: string;
}

interface PaymnetRequestDetails {
  total: {
    label: string;
    amount: {
      currency: string;
      value: string;
    };
  };
}

@Component({
  components: {
    CoinflowCardNumberInput,
    CoinflowCvvInput,
    VueGoogleAutocomplete,
    CoinflowGooglePayButton,
    CoinflowApplePayButton,
  },
})
export default class Coinflow extends Vue {
  env: string = process.env.coinflowEnv || 'sandbox';
  baseUrl: string =
    process.env.coinflowUrl || 'https://api-sandbox.coinflow.cash';
  merchantId: string = process.env.coinflowMerchantId || 'galagames';
  loading: boolean = true;
  style: object = {
    position: 'fixed',
    top: '-10000px',
  };
  expire: string = '';
  name: string = '';
  address: string = '';
  city: string = '';
  state: string = '';
  zip: string = '';
  country: string = 'US';
  countries: { value: string; text: any }[] = [];
  countryAbbr: Record<string, string> = {};
  orderId: string = '';
  coinflowStyle: string = '';
  formValid: boolean = false;
  didFirstValidation: boolean = true;
  didFirstCardNumberValidation: boolean = true;
  validCardNumber: CoinflowCardValidationResponse = {
    isValid: true,
    isCvvValid: true,
  };
  addressDidBlur: boolean = false;
  addressManually: boolean = false;
  cvvDidBlur: boolean = false;

  CoinflowPaymentMethodKey: any = CoinflowPaymentMethodKey;
  paymentMethods: Record<string, CoinflowPaymentMethod> = {};
  paymentMethodOptions: CoinflowPaymentMethod[] = [];
  paymentMethod: string = CoinflowPaymentMethodKey.CreditCard;

  async created() {
    this.countries = Object.keys(countries).map(key => {
      const value = (countries as any)[key];
      this.countryAbbr[value] = key;
      return {
        value: key,
        text: value,
      };
    });

    this.paymentMethods[CoinflowPaymentMethodKey.CreditCard] = {
      key: CoinflowPaymentMethodKey.CreditCard,
      label: 'Card',
      icon: 'https://static.gala.games/gala-name-tokens/credit-card.svg',
      alt: 'credit card icon',
    };

    this.paymentMethods[CoinflowPaymentMethodKey.GooglePay] = {
      key: CoinflowPaymentMethodKey.GooglePay,
      label: 'Google Pay',
      icon: require('~/assets/icons/google-pay.svg'),
      alt: 'google pay icon',
    };

    this.paymentMethods[CoinflowPaymentMethodKey.ApplePay] = {
      key: CoinflowPaymentMethodKey.ApplePay,
      label: 'Apple Pay',
      icon: require('~/assets/icons/apple-pay.svg'),
      alt: 'apple pay icon',
    };

    this.paymentMethodOptions.push(
      this.paymentMethods[CoinflowPaymentMethodKey.CreditCard],
    );

    if (window.PaymentRequest) {
      const details: PaymnetRequestDetails = {
        total: {
          label: 'Total',
          amount: {
            currency: 'USD',
            value: '0.00',
          },
        },
      };

      try {
        const googlePaymentRequest: PaymentRequest = this.createGooglePaymentRequest(
          details,
        );

        if (await googlePaymentRequest.canMakePayment()) {
          this.paymentMethodOptions.push(
            this.paymentMethods[CoinflowPaymentMethodKey.GooglePay],
          );
        }
      } catch (e) {
        this.$sentry.captureException(e);
      }
    }

    // @ts-ignore
    if (window.ApplePaySession && ApplePaySession.canMakePayments()) {
      this.paymentMethodOptions.push(
        this.paymentMethods[CoinflowPaymentMethodKey.ApplePay],
      );
    }
  }

  mounted() {
    const cssDeclaration = window.getComputedStyle(
      (this.$refs.expireRef as any).$refs.input as Element,
    );
    this.coinflowStyle = Array.from(cssDeclaration)
      .filter(property => property !== 'width') // Filter out the 'width' property
      .map(property => {
        return `${property}: ${cssDeclaration.getPropertyValue(property)};`;
      })
      .join(' ');
  }

  get validAddress() {
    return this.address && this.city && this.state && this.zip && this.country;
  }

  get addressButtonText() {
    return this.addressManually
      ? this.$t('components.coinflow.addressButtonText.clear')
      : this.$t('components.coinflow.addressButtonText.manual');
  }

  get cardNumberRules() {
    const rules = {
      default: this.$t('components.coinflow.cardNumberRule.default'),
      required: this.$t('components.coinflow.cardNumberRule.required'),
      format: this.$t('components.coinflow.cardNumberRule.format'),
    } as any;
    return rules[this.validCardNumber?.validator || 'default'];
  }

  get cardCvvRules() {
    const rules = {
      default: this.$t('components.coinflow.cvvRule.default'),
      required: this.$t('components.coinflow.cvvRule.required'),
      format: this.$t('components.coinflow.cvvRule.format'),
    } as any;
    return rules[this.validCardNumber?.cvvValidator || 'default'];
  }

  get expirationDateRules() {
    return [
      (v: string) =>
        !!v || this.$t('components.coinflow.expirationDateRule.required'),
      (v: string) =>
        this.isValidExpirationFormat(v) ||
        this.$t('components.coinflow.expirationDateRule.format'),
      (v: string) =>
        this.isValidExpirationDate(v) ||
        this.$t('components.coinflow.expirationDateRule.invalid'),
    ];
  }

  get nameRules() {
    return [
      (v: string) => !!v || this.$t('components.coinflow.nameRule.required'),
      (v: string) => {
        const names = v.trim().split(' ');
        return (
          names.length >= 2 || this.$t('components.coinflow.nameRule.invalid')
        );
      },
      (v: string) => {
        const firstName = v.trim().split(' ')[0] || '';
        return (
          firstName.length >= 2 ||
          this.$t('components.coinflow.nameRule.firstLength')
        );
      },
      (v: string) => {
        const names = v.trim().split(' ');
        const lastName = names.length > 1 ? names[names.length - 1] : '';
        return (
          lastName.length >= 2 ||
          this.$t('components.coinflow.nameRule.lastLength')
        );
      },
    ];
  }

  get streetRules() {
    return [
      (v: string) => !!v || this.$t('components.coinflow.streetRule.required'),
    ];
  }

  get cityRules() {
    return [
      (v: string) => !!v || this.$t('components.coinflow.cityRule.required'),
    ];
  }

  get addressRules() {
    const rules = {
      default: this.$t('components.coinflow.streetRule.required'),
      required: this.$t('components.coinflow.streetRule.required'),
    } as any;
    return rules[this.validAddress ? 'required' : 'default'];
  }

  get stateRules() {
    return [
      (v: string) => !!v || this.$t('components.coinflow.stateRule.required'),
      (v: string) =>
        (v && v.length == 2) ||
        this.$t('components.coinflow.stateRule.invalid'),
    ];
  }

  get zipRules() {
    return [
      (v: string) => !!v || this.$t('components.coinflow.zipRule.required'),
    ];
  }

  get countryRules() {
    return [
      (v: string) => !!v || this.$t('components.coinflow.countryRule.required'),
    ];
  }

  get isCreditCardOnly() {
    return this.paymentMethodOptions.length === 1;
  }

  get isCreditCardMethod() {
    return this.paymentMethod === CoinflowPaymentMethodKey.CreditCard;
  }

  get isGoolgePayMethod() {
    return this.paymentMethod === CoinflowPaymentMethodKey.GooglePay;
  }

  get isApplePayMethod() {
    return this.paymentMethod === CoinflowPaymentMethodKey.ApplePay;
  }

  handleAddressBlur() {
    this.addressDidBlur = true;
  }

  handleAddressManually() {
    this.addressManually = !this.addressManually;

    if (!this.addressManually) {
      this.address = '';
      this.city = '';
      this.state = '';
      this.zip = '';
      this.country = 'US';
    }
  }

  handlePlaceChange(newPlace: any) {
    this.address = `${newPlace.street_number} ${newPlace.route}`;
    this.city =
      newPlace.locality || newPlace.sublocality || newPlace.sublocality_level_1;
    this.state = newPlace.administrative_area_level_1;
    this.zip = newPlace.postal_code;
    this.country = this.countryAbbr[newPlace.country];
  }

  handleCardNumberInputBlur() {}

  handleCardCvvInputBlur() {
    this.cvvDidBlur = true;
  }

  handleCardNumberInputLoaded(newValue: boolean) {
    if (newValue) {
      this.style = {};
      this.loading = false;
    }
  }

  handleCardNumberInputValidate(newValue: any) {
    this.validCardNumber = newValue;
    this.didFirstCardNumberValidation = true;
  }

  handleCardNumberInputValidated(newValue: CoinflowCardValidationResponse) {
    if (this.didFirstCardNumberValidation) {
      this.validCardNumber = newValue;
    }
  }

  async handleSubmit() {
    if (this.paymentMethod !== CoinflowPaymentMethodKey.CreditCard) {
      return true;
    }

    (this.$refs.form as any).validate();

    this.didFirstValidation = true;
    this.cvvDidBlur = true;
    this.addressDidBlur = true;

    const cardNumberInputRef = this.$refs
      .cardNumberInput as CoinflowCardNumberInput;
    this.validCardNumber = await cardNumberInputRef.validate();

    return (
      this.formValid &&
      this.validCardNumber.isValid &&
      this.validCardNumber.isCvvValid
    );
  }

  createApplePaymentSession(lineItem: ApplePayJS.ApplePayLineItem) {
    const merchantCapabilities: ApplePayJS.ApplePayMerchantCapability[] = [
      'supports3DS',
      'supportsCredit',
      'supportsDebit',
    ];

    const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
      countryCode: 'US',
      currencyCode: 'USD',
      supportedNetworks: ['visa', 'masterCard', 'amex'],
      merchantCapabilities: merchantCapabilities,
      requiredShippingContactFields: ['email'],
      requiredBillingContactFields: [
        'email',
        'name',
        'postalAddress',
      ] as ApplePayJS.ApplePayContactField[],
      total: lineItem,
    };

    return new ApplePaySession(3, paymentRequest);
  }

  createGooglePaymentRequest(details: PaymnetRequestDetails) {
    const methodData = [
      {
        supportedMethods: 'https://google.com/pay',
        data: this.getGooglePaymentsConfiguration(),
      },
    ];

    return new PaymentRequest(methodData, details);
  }

  getGooglePaymentsConfiguration() {
    const allowedCardNetworks = ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA'];
    const allowedCardAuthMethods = ['PAN_ONLY', 'CRYPTOGRAM_3DS'];

    return {
      environment: process.env.coinflowGooglePayEnv,
      apiVersion: 2,
      apiVersionMinor: 0,
      emailRequired: true,
      merchantInfo: {
        merchantId: process.env.coinflowGooglePayMerchantId,
        merchantName: process.env.coinflowGooglePayMerchantName,
      },
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: allowedCardAuthMethods,
            allowedCardNetworks: allowedCardNetworks,
            assuranceDetailsRequired: true,
            billingAddressRequired: true,
            billingAddressParameters: {
              format: 'FULL',
            },
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: {
              gateway: process.env.coinflowGooglePayGateway,
              gatewayMerchantId:
                process.env.coinflowGooglePayGatewayMerchangeId,
            },
          },
        },
      ],
    };
  }

  isValidExpirationFormat(date: string) {
    // Regex to check if date is in MM / YY format
    const regex = /^(0[1-9]|1[0-2])\/\d{2}$/;
    return regex.test(date);
  }

  isValidExpirationDate(date: string) {
    if (!this.isValidExpirationFormat(date)) return false;

    const [month, year] = date.split('/').map(Number);
    const now = new Date();
    const currentMonth = now.getMonth() + 1; // Months are 0-based in JS
    const currentYear = now.getFullYear() % 100; // Last two digits of the year

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return false;
    }

    return true;
  }

  expireFormat(event: KeyboardEvent) {
    let code = event.keyCode;
    let allowedKeys = [8];

    if (allowedKeys.indexOf(code) !== -1) {
      return;
    }

    this.expire = this.expire
      .replace(/^([1-9]\/|[2-9])$/g, '0$1/') // 3 > 03/
      .replace(/^(0[1-9]|1[0-2])$/g, '$1/') // 11 > 11/
      .replace(/^([0-1])([3-9])$/g, '0$1/$2') // 13 > 01/3
      .replace(/^(0?[1-9]|1[0-2])([0-9]{2})$/g, '$1/$2') // 141 > 01/41
      .replace(/^([0]+)\/|[0]+$/g, '0') // 0/ > 0 and 00 > 0
      .replace(/[^\d\/]|^[\/]*$/g, '') // To allow only digits and `/`
      .replace(/\/\//g, '/'); // Prevent entering more than 1 `/`

    this.$vuetify.theme.themes.light.accentBlue;
  }

  async sendCardPayment(payload: {
    user: IUser;
    product: IGameItemForSaleByGala | null;
    quantity: number;
    total: number;
  }) {
    try {
      const walletAddress = `0x0000000000000000${payload.user.id}`;
      const cardNumberInputRef = this.$refs
        .cardNumberInput as CoinflowCardNumberInput;
      const coinflowToken = await cardNumberInputRef.getToken();
      const cents = (payload.total * 100).toFixed(0);
      // @ts-ignore
      const deviceId = window.nSureSDK.getDeviceId();
      const expire = this.expire.split('/');
      const name = this.name.split(' ');
      const firstName = name.shift();
      const lastName = name.join(' ');

      const card = {
        cardToken: coinflowToken.token,
        expMonth: expire[0],
        expYear: expire[1],
        email: payload.user.email,
        firstName: firstName,
        lastName: lastName,
        address1: this.address,
        city: this.city,
        state: this.state,
        zip: this.zip, // 99999 or 00000 failures
        country: this.country,
      };

      const cardType = coinflowToken?.cardType || '';
      const last4 = coinflowToken?.lastFour || '';

      const options = {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          'x-coinflow-auth-wallet': walletAddress,
          'x-coinflow-auth-blockchain': 'polygon',
          'x-device-id': deviceId,
        },
        body: JSON.stringify({
          subtotal: {
            cents: cents,
          },
          card: card,
          saveCard: false,
          settlementType: 'Bank',
          webhookInfo: {
            orderId: this.orderId,
            paymentMethod: {
              cardType,
              last4,
            },
            product: {
              productId: payload.product?.productId,
              baseId: payload.product?.baseId,
              game: payload.product?.game,
              name: payload.product?.name,
              category: payload.product?.category,
              type: payload.product?.type,
              quantity: payload.quantity,
            },
          },
          chargebackProtectionData: {
            productName: payload.product?.name,
            productType: CoinflowProductType.IN_GAME_PRODUCT,
            quantity: payload.quantity,
            rawProductData: {
              orderId: this.orderId,
              productId: payload.product?.productId,
              baseId: payload.product?.baseId,
              game: payload.product?.game,
              name: payload.product?.name,
              category: payload.product?.category,
              type: payload.product?.type,
            },
          },
        }),
      };

      const response = await fetch(
        `${this.baseUrl}/api/checkout/card/${this.merchantId}`,
        options,
      );
      const responseJson = await response.json();

      if (responseJson.code) {
        this.orderId = '';
      }

      return responseJson;
    } catch (e) {
      this.$sentry.captureException(e);
    }
  }

  async sendGoolgePayment(payload: {
    user: IUser;
    product: IGameItemForSaleByGala | null;
    quantity: number;
    total: number;
    googlePayResponse: PaymentResponse;
  }) {
    try {
      const walletAddress = `0x0000000000000000${payload.user.id}`;
      const cents = (payload.total * 100).toFixed(0);
      // @ts-ignore
      const deviceId = window.nSureSDK.getDeviceId();

      const {
        cardDetails: last4,
        cardNetwork: cardType,
      } = payload.googlePayResponse?.details?.paymentMethodData?.info;

      const options = {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          'x-coinflow-auth-wallet': walletAddress,
          'x-coinflow-auth-blockchain': 'polygon',
          'x-device-id': deviceId,
        },
        body: JSON.stringify({
          subtotal: {
            cents: cents,
          },
          settlementType: 'Bank',
          paymentData: payload.googlePayResponse?.details,
          webhookInfo: {
            orderId: this.orderId,
            paymentMethod: {
              cardType,
              last4,
            },
            product: {
              productId: payload.product?.productId,
              baseId: payload.product?.baseId,
              game: payload.product?.game,
              name: payload.product?.name,
              category: payload.product?.category,
              type: payload.product?.type,
              quantity: payload.quantity,
            },
          },
          chargebackProtectionData: {
            productName: payload.product?.name,
            productType: CoinflowProductType.IN_GAME_PRODUCT,
            quantity: payload.quantity,
            rawProductData: {
              orderId: this.orderId,
              productId: payload.product?.productId,
              baseId: payload.product?.baseId,
              game: payload.product?.game,
              name: payload.product?.name,
              category: payload.product?.category,
              type: payload.product?.type,
            },
          },
        }),
      };

      const response = await fetch(
        `${this.baseUrl}/api/checkout/google-pay/${this.merchantId}`,
        options,
      );
      const responseJson = await response.json();

      if (responseJson.code) {
        this.orderId = '';
      }

      return responseJson;
    } catch (e) {
      this.$sentry.captureException(e);
    }
  }

  async approvelyPaymentAuthorize(
    applePayResponse: ApplePayJS.ApplePayPayment,
  ) {
    const options = {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        PKeyCompany: process.env.coinflowApplePKeyCompany,
        AppleMerchantId: process.env.coinflowAppleMerchantId,
        ApplePayPayment: applePayResponse,
      }),
    };
    const response = await fetch(
      'https://app.approvely.net/api/public/applepay/paymentauthorized',
      options,
    );

    return await response.json();
  }

  async sendApplePayment(payload: {
    user: IUser;
    product: IGameItemForSaleByGala | null;
    quantity: number;
    total: number;
    applePayResponse: ApplePayJS.ApplePayPayment;
  }) {
    try {
      const walletAddress = `0x0000000000000000${payload.user.id}`;
      const cents = (payload.total * 100).toFixed(0);
      // @ts-ignore
      const deviceId = window.nSureSDK.getDeviceId();

      const authPaymentToken = await this.approvelyPaymentAuthorize(
        payload.applePayResponse,
      );

      const [
        cardType,
        last4,
      ] = payload.applePayResponse?.token?.paymentMethod?.displayName?.split(
        ' ',
      ) || ['', ''];

      const options = {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          'x-coinflow-auth-wallet': walletAddress,
          'x-coinflow-auth-blockchain': 'polygon',
          'x-device-id': deviceId,
        },
        body: JSON.stringify({
          subtotal: {
            cents: cents,
          },
          settlementType: 'Bank',
          applePayResponse: {
            token: authPaymentToken,
            raw_response: {
              details: payload.applePayResponse,
            },
          },
          webhookInfo: {
            orderId: this.orderId,
            paymentMethod: {
              cardType,
              last4,
            },
            product: {
              productId: payload.product?.productId,
              baseId: payload.product?.baseId,
              game: payload.product?.game,
              name: payload.product?.name,
              category: payload.product?.category,
              type: payload.product?.type,
              quantity: payload.quantity,
            },
          },
          chargebackProtectionData: {
            productName: payload.product?.name,
            productType: CoinflowProductType.IN_GAME_PRODUCT,
            quantity: payload.quantity,
            rawProductData: {
              orderId: this.orderId,
              productId: payload.product?.productId,
              baseId: payload.product?.baseId,
              game: payload.product?.game,
              name: payload.product?.name,
              category: payload.product?.category,
              type: payload.product?.type,
            },
          },
        }),
      };

      const response = await fetch(
        `${this.baseUrl}/api/checkout/apple-pay/${this.merchantId}`,
        options,
      );
      const responseJson = await response.json();

      if (responseJson.code) {
        this.orderId = '';
      }

      return responseJson;
    } catch (e) {
      this.$sentry.captureException(e);
    }
  }

  async validateApplePayMerchant(validationURL: string) {
    const options = {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        PKeyCompany: process.env.coinflowApplePKeyCompany,
        AppleMerchantId: process.env.coinflowAppleMerchantId,
        ValidationUrl: validationURL,
        DomainName: window.location.hostname,
      }),
    };
    const response = await fetch(
      'https://app.approvely.net/api/public/applepay/validatemerchant',
      options,
    );
    return await response.json();
  }

  async handleApplePay(payload: {
    user: IUser;
    product: IGameItemForSaleByGala | null;
    quantity: number;
    expectedPrice: number;
    totpToken: string;
    acceptedDocuments: IDocumentsAccepted;
    priceAdjustmentCode?: string;
    productContext?: { nameToken?: string };
  }) {
    return new Promise<any>(async resolve => {
      try {
        const applePaySession = await this.createApplePaymentSession({
          label: 'Total',
          amount: payload.expectedPrice.toString(),
        });

        applePaySession.oncancel = (event: ApplePayJS.Event) => {
          resolve({
            message:
              'There was an error processing the request, please try again',
          });
        };

        applePaySession.onvalidatemerchant = async (
          event: ApplePayJS.ApplePayValidateMerchantEvent,
        ) => {
          const merchantValidation = await this.validateApplePayMerchant(
            event.validationURL,
          );

          if (!this.orderId) {
            try {
              const pendingOrder = await this.createOrder({
                productId: payload?.product?.productId,
                quantity: payload.quantity,
                expectedPrice: payload.expectedPrice,
                totpToken: payload.totpToken,
                acceptedDocuments: payload.acceptedDocuments,
                priceAdjustmentCode: payload?.priceAdjustmentCode,
                productContext: payload?.productContext,
              });
              this.orderId = pendingOrder?.data?.coinflowPurchase?.orderId;
            } catch (e) {
              this.$sentry.captureException(e);
              applePaySession.completeMerchantValidation({});
            }
          }

          applePaySession.completeMerchantValidation(merchantValidation);
        };

        applePaySession.onpaymentauthorized = async (
          event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
        ) => {
          applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS);

          resolve(
            await this.sendApplePayment({
              user: payload.user,
              product: payload.product,
              quantity: payload.quantity,
              total: payload.expectedPrice,
              applePayResponse: event.payment,
            }),
          );
        };

        applePaySession.begin();
      } catch (e) {
        this.$sentry.captureException(e);
        resolve({
          message:
            'There was an error processing the request, please try again',
        });
      }
    });
  }

  async createOrder(payload: {
    productId: string | undefined;
    quantity: number;
    expectedPrice: number;
    totpToken: string;
    acceptedDocuments: IDocumentsAccepted;
    priceAdjustmentCode?: string;
    productContext?: { nameToken?: string };
  }) {
    return await this.$apollo.query({
      query: CoinflowPurchase,
      variables: {
        ...payload,
        acceptedDocuments: payload.acceptedDocuments.map(
          ({ documentType, versionAccepted }) => ({
            documentType,
            versionAccepted,
          }),
        ),
        orderContext: getParsedCookie('utmVariables') || undefined,
      },
    });
  }

  async confirmCoinflowPayment(payload: {
    user: IUser;
    product: IGameItemForSaleByGala | null;
    quantity: number;
    expectedPrice: number;
    totpToken: string;
    acceptedDocuments: IDocumentsAccepted;
    priceAdjustmentCode?: string;
    productContext?: { nameToken?: string };
  }) {
    try {
      let coinflowPayResponse;

      if (this.paymentMethod === CoinflowPaymentMethodKey.CreditCard) {
        if (!this.orderId) {
          const pendingOrder = await this.createOrder({
            productId: payload?.product?.productId,
            quantity: payload.quantity,
            expectedPrice: payload.expectedPrice,
            totpToken: payload.totpToken,
            acceptedDocuments: payload.acceptedDocuments,
            priceAdjustmentCode: payload?.priceAdjustmentCode,
            productContext: payload?.productContext,
          });
          this.orderId = pendingOrder?.data?.coinflowPurchase?.orderId;
        }

        coinflowPayResponse = await this.sendCardPayment({
          user: payload.user,
          product: payload.product,
          quantity: payload.quantity,
          total: payload.expectedPrice,
        });
      } else if (this.paymentMethod === CoinflowPaymentMethodKey.GooglePay) {
        if (!this.orderId) {
          const pendingOrder = await this.createOrder({
            productId: payload?.product?.productId,
            quantity: payload.quantity,
            expectedPrice: payload.expectedPrice,
            totpToken: payload.totpToken,
            acceptedDocuments: payload.acceptedDocuments,
            priceAdjustmentCode: payload?.priceAdjustmentCode,
            productContext: payload?.productContext,
          });
          this.orderId = pendingOrder?.data?.coinflowPurchase?.orderId;
        }

        const googlePayResponse: PaymentResponse = await this.createGooglePaymentRequest(
          {
            total: {
              label: 'Total',
              amount: {
                currency: 'USD',
                value: payload.expectedPrice.toString(),
              },
            },
          },
        ).show();

        googlePayResponse.complete('success');

        coinflowPayResponse = await this.sendGoolgePayment({
          user: payload.user,
          product: payload.product,
          quantity: payload.quantity,
          total: payload.expectedPrice,
          googlePayResponse: googlePayResponse,
        });
      } else if (this.paymentMethod === CoinflowPaymentMethodKey.ApplePay) {
        coinflowPayResponse = await this.handleApplePay(payload);
      }

      if (coinflowPayResponse.code) {
        this.orderId = '';
        return {
          success: false,
          message: coinflowPayResponse.message,
        };
      } else if (!coinflowPayResponse.paymentId) {
        return {
          success: false,
          message: coinflowPayResponse.message,
        };
      }

      return {
        success: true,
        paymentId: coinflowPayResponse?.paymentId,
        orderId: this.orderId,
      };
    } catch (e) {
      this.$sentry.captureException(e);
      return {
        success: false,
        message: 'There was an error processing the request, please try again',
      };
    }
  }
}
